import { httpClient } from '../api/http-client';
import { API_ENDPOINTS } from '../../constants/api';

export interface Cliente {
  id: string;
  nombres: string;
  apellidos: string;
  telefono: string;
  email: string;
  numeroDocumento?: string;
  direccion?: string;
  consentimientoDatos: boolean;
  creadoEn: string;
  actualizadoEn: string;
  estado: string;
}

export interface CreateClienteDto {
  nombres: string;
  apellidos: string;
  telefono: string;
  email: string;
  numeroDocumento?: string;
  direccion?: string;
  consentimientoDatos?: boolean;
}

export interface UpdateClienteDto {
  nombres?: string;
  apellidos?: string;
  telefono?: string;
  email?: string;
  numeroDocumento?: string;
  direccion?: string;
  consentimientoDatos?: boolean;
}

export interface RegistroClienteDto extends CreateClienteDto {
  // Formulario de registro público
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface PaginatedClientesResponse {
  data: Cliente[];
  meta: PaginationMeta;
}

export class ClientesService {
  // Registro público de cliente
  static async registrarCliente(data: RegistroClienteDto): Promise<{ success: boolean; message: string; data?: Cliente }> {
    const response = await httpClient.post<{ success: boolean; message: string; data?: Cliente }>(
      API_ENDPOINTS.clientes.create,
      data
    );
    return response.data;
  }

  static async createCliente(data: CreateClienteDto): Promise<Cliente> {
    const response = await httpClient.post<Cliente>(
      API_ENDPOINTS.clientes.list,
      data
    );
    return response.data;
  }

  static async getCliente(id: string): Promise<Cliente> {
    const response = await httpClient.get<Cliente>(
      API_ENDPOINTS.clientes.detail(id)
    );
    return response.data;
  }

  static async updateCliente(id: string, data: UpdateClienteDto): Promise<Cliente> {
    const response = await httpClient.put<Cliente>(
      API_ENDPOINTS.clientes.update(id),
      data
    );
    return response.data;
  }

  static async deleteCliente(id: string): Promise<void> {
    await httpClient.delete(API_ENDPOINTS.clientes.delete(id));
  }

  static async toggleStatus(id: string): Promise<Cliente> {
    const response = await httpClient.post<Cliente>(
      API_ENDPOINTS.clientes.toggleStatus(id)
    );
    return response.data;
  }

  static async getClientes(page = 1, limit = 10, search?: string): Promise<PaginatedClientesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      params.append('search', search);
    }

    const response = await httpClient.get<PaginatedClientesResponse>(
      `${API_ENDPOINTS.clientes.list}?${params.toString()}`
    );
    return response.data;
  }

  static async getStats(): Promise<{ totalClientes: number; nuevosEsteMes: number; activos: number; inactivos: number }> {
    const response = await httpClient.get<{ totalClientes: number; nuevosEsteMes: number; activos: number; inactivos: number }>(
      API_ENDPOINTS.clientes.stats
    );
    return response.data;
  }

  static async verificarCuenta(email: string, token: string, password: string): Promise<{ success: boolean; message: string }> {
    const response = await httpClient.post<{ success: boolean; message: string }>(
      `${API_ENDPOINTS.clientes.list}/verificar`,
      { email, token, password }
    );
    return response.data;
  }

  static async reenviarCodigo(email: string): Promise<{ success: boolean; message: string }> {
    const response = await httpClient.post<{ success: boolean; message: string }>(
      `${API_ENDPOINTS.clientes.list}/reenviar-codigo`,
      { email }
    );
    return response.data;
  }

  static async solicitarResetPassword(email: string): Promise<{ success: boolean; message: string }> {
    const response = await httpClient.post<{ success: boolean; message: string }>(
      `${API_ENDPOINTS.clientes.list}/recuperar-contrasena`,
      { email }
    );
    return response.data;
  }

  static async resetPassword(email: string, token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await httpClient.post<{ success: boolean; message: string }>(
      `${API_ENDPOINTS.clientes.list}/restablecer-contrasena`,
      { email, token, newPassword }
    );
    return response.data;
  }
}
